import { FC, useEffect, useState } from 'react'

import { connect } from 'react-redux'
import styled from 'styled-components'

import * as constants from 'app/bond_cover/agency/constants'
import { parseMultipleOfficeObjectToArray } from 'app/bond_cover/agency/utils'
import {
  Box,
  Button,
  Dropdown,
  Flex,
  HorizontalLine,
} from 'app/components/design-system-components'
import { ArrowDropDownRounded } from 'app/components/design-system-components/icons/navigation'
import { CheckboxesList } from 'app/components/design-system-components/list-filters/CheckboxesList'
import {
  fontSizes,
  fontWeight,
} from 'app/components/design-system-components/styles/variables'
import { filtersIds } from 'app/dashboard/team_activity/ActivityFilters/filters.config'
import { theme } from 'app/match/applicationReportPDF/assets/theme'
import { getAgencySettings } from 'app/services/http/agency'
import * as roleHelpers from 'app/utils/roles'
import { TRUE_STR } from 'app/utils/strings/helpers'

export const DropdownBodyWrapper = styled(Box)`
  box-shadow: 0 ${theme.space[1]}px ${theme.dividedSpace[6]}px 0
    ${theme.colors.dropdownDroppedShadow};
  background-color: ${theme.colors.white};

  .dropdown-header {
    font-weight: ${fontWeight[6]};
    font-size: ${theme.space[4]}px;
  }

  .dropdown-list {
    input[type='checkbox'] {
      margin-right: ${theme.space[1]}px;
    }

    label > span {
      font-size: ${fontSizes[4]}px;
    }
  }

  .dropdown-list:last-child {
    .form-checkbox {
      padding-bottom: ${theme.baseSpace}px;
    }
  }
`

export const StyledDropdownBodyWrapper = styled(DropdownBodyWrapper)`
  .dropdown-list:first-child {
    .form-checkbox {
      padding-top: ${theme.baseSpace}px;
    }
  }
`

const toDropdownType = (item: any) => ({
  option: item?.agencyInfo?.agencyGUID,
  optionMeta: {
    id: item?.agencyInfo?.agencyGUID,
    displayText: item?.agencyInfo?.agencyName,
    noSeparator: true,
  },
})

const toDropdownTypeCurrentTeam = (currentTeam: any) => ({
  option: currentTeam.guid,
  optionMeta: {
    id: currentTeam.guid,
    displayText: currentTeam.name,
    noSeparator: true,
  },
})

interface IMultiTeamReportingDropdownProps {
  teamId: any
  updateFilter: any
  currentTeam: any
  currentAgency: any
  multiTeamOptions: { multiTeamOptions?: boolean; singleSelectTeam?: boolean }
  isOnlyMultiTeamReportingFilter: any
  dropDownWidth: any
}

type TeamItemType = {
  agencyInfo: {
    agencyGUID: string
    agencyName: string
  }
}

const MultiTeamReportingDropdown: FC<IMultiTeamReportingDropdownProps> = ({
  teamId,
  updateFilter,
  currentTeam,
  currentAgency,
  multiTeamOptions,
  isOnlyMultiTeamReportingFilter,
  dropDownWidth,
}) => {
  const [teams, setTeams] = useState<TeamItemType[]>([])
  const [enabled, setEnabled] = useState(false)

  const [checkedOptions, setCheckedOptions] = useState({
    [currentTeam?.guid]: true,
  })
  const isMultiTeam = !multiTeamOptions?.singleSelectTeam
  const checkIfUserIsAdmin = currentAgency
    ? roleHelpers.isUserAnAdmin(currentAgency)
    : false

  useEffect(() => {
    getAgencySettings(teamId, 'multipleoffice').then((settings: any) => {
      const isEnabled =
        settings.settingInfo?.multipleoffice
          ?.multiple_office_reporting_owner_enabled === TRUE_STR

      setEnabled(isEnabled)
      if (isEnabled) {
        const options = parseMultipleOfficeObjectToArray(
          settings.settingInfo?.multipleoffice,
          constants.agency_reporting_owner_guid,
          constants.agency_reporting_owner_name,
        )
        setTeams(options)
      }
    })
  }, [teamId])

  const options = [toDropdownTypeCurrentTeam(currentTeam)].concat(
    teams.map(toDropdownType),
  )

  const sizeSelected = Object.keys(checkedOptions).length
  const isAll = sizeSelected === options.length

  const firstSelected = options.find((item) => {
    return checkedOptions[item.option]
  })

  const handleApplyFilter = (teams: any) => {
    updateFilter(filtersIds.teams, {
      teams,
    })
  }

  const onSubmit = (dropdownControls: any) => {
    handleApplyFilter(
      options
        .filter((option) => checkedOptions[option.option])
        .map((item) => ({
          agencyInfo: {
            agencyGUID: item.option,
            agencyName: item.optionMeta.displayText,
          },
        })),
    )
    dropdownControls.toggle()
  }

  useEffect(() => {
    if (Object.keys(checkedOptions).length === 0) {
      setCheckedOptions({
        [currentTeam.guid]: true,
      })
    }
  }, [checkedOptions, currentTeam])

  if (!enabled) {
    return null
  }

  const getDropdownButtonText = () => {
    let text = 'All teams'

    if (!isAll) {
      if (sizeSelected === 1) {
        text = firstSelected?.optionMeta.displayText
      } else {
        text = `(${sizeSelected}) teams`
      }
    }

    return text
  }

  const onCheckBoxClickedMultiTeamOption = (checked: boolean) => {
    if (checked) {
      setCheckedOptions(
        options.reduce(
          (acc, cur) => ({
            ...acc,
            [cur.option]: true,
          }),
          {},
        ),
      )
    } else {
      setCheckedOptions({})
    }
  }

  const onCheckBoxClickedTeamOption = (checked: boolean, option: any) => {
    if (isMultiTeam) {
      if (checked) {
        checkedOptions[option.option] = true
      } else {
        delete checkedOptions[option.option]
      }
      setCheckedOptions({
        ...checkedOptions,
      })
    } else {
      setCheckedOptions(
        checked
          ? {
              [option.option]: true,
            }
          : {},
      )
    }
  }

  return (
    <Flex alignItems="center">
      <Dropdown
        triggerWrapperComponent={({ dropdownControls }: any) => (
          <Button
            variant="outline"
            disabled={!checkIfUserIsAdmin}
            onClick={() =>
              checkIfUserIsAdmin ? dropdownControls.toggle() : false
            }
            {...(dropDownWidth && { width: dropDownWidth })}
            height={theme.height[18] + 'px'}
          >
            <Flex justifyContent="space-between">
              <Box className={'ellipsis'}>{getDropdownButtonText()}</Box>
              <Box
                as={ArrowDropDownRounded}
                ml={theme.space[3] + 'px'}
                fontSize={theme.fontSizes.pExtraLarge18}
              />
            </Flex>
          </Button>
        )}
        dropdownComponent={({ dropdownControls }: any) => (
          <StyledDropdownBodyWrapper>
            {isMultiTeam && (
              <>
                <Box fontSize="12px" p="1rem">
                  All teams
                </Box>
                <HorizontalLine mt="1px" />
                <Box>
                  <CheckboxesList
                    options={[
                      {
                        option: 'all',
                        optionMeta: {
                          id: 'all',
                          displayText: 'All teams',
                          noSeparator: true,
                        },
                      },
                    ]}
                    checkedOptions={{
                      all: isAll,
                    }}
                    onCheckBoxClicked={onCheckBoxClickedMultiTeamOption}
                    containerProps={{
                      className: 'dropdown-list',
                    }}
                  />
                </Box>
              </>
            )}

            <Box fontSize="12px" m="1rem">
              Select teams
            </Box>
            <HorizontalLine mt="1px" />
            <Box>
              <CheckboxesList
                options={options}
                checkedOptions={checkedOptions}
                onCheckBoxClicked={onCheckBoxClickedTeamOption}
                containerProps={{
                  className: 'dropdown-list',
                }}
              />
            </Box>

            <HorizontalLine mt="1px" />

            <Box boxShadow={theme.boxShadows.black} p={theme.space[3] + 'px'}>
              <Button
                sizeVariant="regular"
                width="100%"
                onClick={onSubmit.bind(null, dropdownControls)}
              >
                Apply Filter
              </Button>
            </Box>
          </StyledDropdownBodyWrapper>
        )}
        topSpaceInPx={9}
        {...(!isOnlyMultiTeamReportingFilter && {
          mr: 3,
        })}
      />
    </Flex>
  )
}

const mapStateToProps = ({ session }: { session: any }) => ({
  currentTeam: session.currentTeam,
  currentAgency: session.currentAgency,
})

const mapDispatchToProps = () => ({})

export default connect(
  mapStateToProps,
  mapDispatchToProps,
)(MultiTeamReportingDropdown)
